import { useEffect, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { CITY } from '@city'
import styles from './PhonePromptCard.module.css'

/**
 * The phone prompt's frame (Figma "app handoff dialog", 351 × auto, radius
 * 24, over a dimmed map): the app icon overlapping the card's top edge, the
 * content, and two equal-width buttons. The primary is a link to the store
 * (`/go/` tracking link); the secondary closes the prompt. Their order is
 * the design's: "Go back to web" above the primary on home and route A, the
 * primary first on route B.
 *
 * A modal dialog: focus moves to the dialog itself (no ring on a button the
 * visitor did not pick), Escape is "Go back to web".
 * Mounted only on a phone, so no desktop focus-trap plumbing. Home sits in
 * the middle of the screen, the route prompts at the bottom over the route.
 * The icon is the iOS AppIcon of the city, light / dark by the site theme.
 */
export interface PromptAction {
  readonly label: string
}

export function PhonePromptCard({
  labelledBy,
  placement,
  icon,
  contentTop,
  primary,
  primaryHref,
  secondary,
  order,
  onPrimary,
  onSecondary,
  children,
}: {
  readonly labelledBy: string
  readonly placement: 'center' | 'bottom'
  readonly icon: 'large' | 'small'
  readonly contentTop: number
  readonly primary: PromptAction
  readonly primaryHref: string
  readonly secondary: PromptAction
  readonly order: 'secondaryFirst' | 'primaryFirst'
  readonly onPrimary: () => void
  readonly onSecondary: () => void
  readonly children: ReactNode
}): ReactElement {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSecondary()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onSecondary])

  const primaryButton = (
    <a
      className={`${styles.button} ${styles.primary}`}
      href={primaryHref}
      rel="nofollow"
      onClick={onPrimary}
    >
      {primary.label}
    </a>
  )
  const secondaryButton = (
    <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={onSecondary}>
      {secondary.label}
    </button>
  )

  return (
    <div className={`${styles.overlay} ${styles[placement]}`}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        <span className={`${styles.icon} ${styles[icon]}`}>
          <img className={styles.iconLight} src={CITY.appIcon.light} alt="" />
          <img className={styles.iconDark} src={CITY.appIcon.dark} alt="" />
        </span>
        <div className={styles.card} style={{ paddingTop: contentTop }}>
          {children}
          <div className={styles.actions}>
            {order === 'secondaryFirst' ? secondaryButton : primaryButton}
            {order === 'secondaryFirst' ? primaryButton : secondaryButton}
          </div>
        </div>
      </div>
    </div>
  )
}
